'use client'

import {
  getLatLng,
  IAddressBook,
  IMetaWrapper,
  IPlaceQuote,
  searchLocation,
  useUpFetch,
} from '@repo/sdk'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

export type AutocompleteLocation = {
  address_book_id?: number
  address_id?: number
  place_id: string
  place_name: string
}

const search = async (inputValue: string) => {
  if (inputValue?.length > 2) {
    return await searchLocation(inputValue)
  }
}

interface Props {
  isAdmin?: boolean
  onChange: (...event: any[]) => void
  value?: IPlaceQuote
}

export const useLocationInput = ({ onChange, value }: Props) => {
  const upFetch = useUpFetch()

  const { getValues } = useFormContext<{
    company_id?: number
  }>()

  const [query, setQuery] = useState('')

  const params = {
    search: query,
  }

  const { data, isPending } = useQuery({
    enabled: query.length > 0,
    queryFn: async () => {
      const data = await search(query)
      const searchData =
        data?.map(locationData => ({
          address_book_id: undefined,
          place_id: locationData.place_id,
          place_name: locationData.description,
        })) ?? []

      try {
        const res = await upFetch<IMetaWrapper<IAddressBook>>(
          `/companies/${getValues('company_id')}/address-book`,
          {
            params,
          },
        )
        const addressBookData = res.data.map(addressBook => ({
          address_book_id: addressBook.id,
          address_id: addressBook.address.id,
          place_id: addressBook.address.place_id,
          place_name: addressBook.name,
        }))
        return [...addressBookData, ...searchData]
      } catch (error) {
        console.error(error)

        return searchData
      }
    },
    queryKey: ['/companies/address-book', getValues('company_id'), query, params],
  })

  const handleOnChange = async (value?: AutocompleteLocation) => {
    if (!value) onChange(undefined)
    const response = value && (await getLatLng(value.place_id))

    if (response) {
      const data = {
        ...value,
        city:
          response.address_components.find(
            addressComponent =>
              addressComponent.types[0] === 'postal_town' ||
              addressComponent.types[0] === 'locality' ||
              addressComponent.types[0] === 'neighborhood', // For Ireland
          )?.long_name ?? '',
        country: response.address_components.find(
          addressComponent => addressComponent.types[0] === 'country',
        )?.short_name,
        lat: response.location.lat.toString(),
        lng: response.location.lng.toString(),
        name: response.address_components.find(
          addressComponent => addressComponent.types[0] === 'route',
        )?.long_name,
        postcode:
          response.address_components.find(
            addressComponent => addressComponent.types[0] === 'postal_code',
          )?.long_name ?? '',
      }
      onChange(data)
    }
  }

  // Define how each option should be rendered in the dropdown
  const renderOption = (item: AutocompleteLocation) => (
    <div>
      <p>{item.place_name}</p>
      <p className={'text-xs text-neutral-400'}> {item.address_book_id && 'Address book'}</p>
    </div>
  )

  // Define how to extract the search value from each item
  const getSearchValue = (item: AutocompleteLocation) => item.place_name

  // Define what to display in the input after an item is selected
  const getDisplayValue = (item: AutocompleteLocation) => item.place_name

  // Determine the selected value based on the prop 'value'
  const selectedValue = useMemo(() => {
    if (value) {
      return {
        place_id: value.place_id ?? '',
        place_name: value.place_name ?? '',
      }
    }
    return null
  }, [value])

  return {
    data,
    getDisplayValue,
    getSearchValue,
    handleOnChange,
    isPending,
    query,
    renderOption,
    selectedValue,
    setQuery,
  }
}
